import React from 'react';

import type { SupplierDocument, SupplierDocumentType } from '../api';
import { openAuthenticatedFile } from '../../../shared/lib/files';
import { formatCivilDate } from '../utils/dates';
import { selectSupplierDocumentVersions } from '../utils/documents';
import {
  documentDisplayStatus,
  documentTypeByCode,
  documentTypeName,
  documentVersionDescription,
} from './SupplierDocumentUpload';

function documentStatusClass(status?: string) {
  if (status === 'aprovado') return 'bg-emerald-100 text-emerald-700';
  if (status === 'reprovado' || status === 'vencido') return 'bg-red-100 text-red-700';
  if (status === 'a_vencer' || status === 'atualizacao_necessaria') return 'bg-amber-100 text-amber-800';
  if (status === 'enviado') return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-700';
}

function VersionSummary({
  document,
  configuredType,
}: {
  document: SupplierDocument;
  configuredType?: SupplierDocumentType;
}) {
  return (
    <>
      <div className="text-xs text-gray-600">
        {documentVersionDescription(document, configuredType, formatCivilDate)}
      </div>
      <span className={`mt-1 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${documentStatusClass(document.status)}`}>
        {documentDisplayStatus(document, configuredType)}
      </span>
    </>
  );
}

function OpenDocumentButton({ document }: { document: SupplierDocument }) {
  if (!document.fileUrl) return null;
  return (
    <button
      type="button"
      onClick={() => openAuthenticatedFile(document.fileUrl!)}
      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
    >
      Abrir v{document.version}
    </button>
  );
}

export const SupplierDocumentCard: React.FC<{
  documentCode: string;
  documentTypes: SupplierDocumentType[];
  documents: SupplierDocument[];
  required: boolean;
  onRenew: () => void;
  onReview?: (document: SupplierDocument, status: 'aprovado' | 'reprovado') => void;
  reviewing?: boolean;
}> = ({
  documentCode,
  documentTypes,
  documents,
  required,
  onRenew,
  onReview,
  reviewing = false,
}) => {
  const configuredType = documentTypeByCode(documentTypes, documentCode);
  const { current, candidate, history } = selectSupplierDocumentVersions(documents, documentCode);
  const fallbackName = current?.name || candidate?.name || history[0]?.name;
  const name = documentTypeName(documentTypes, documentCode, fallbackName);
  const reviewControls = (document: SupplierDocument) => {
    if (!onReview || document.status !== 'enviado') return null;
    return (
      <>
        <button
          type="button"
          onClick={() => onReview(document, 'aprovado')}
          disabled={reviewing}
          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          Aprovar v{document.version}
        </button>
        <button
          type="button"
          onClick={() => onReview(document, 'reprovado')}
          disabled={reviewing}
          className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          Reprovar v{document.version}
        </button>
      </>
    );
  };

  return (
    <section data-document-type={documentCode} className="rounded-lg border border-gray-200 p-3">
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-sm font-medium text-gray-900">{name}</h4>
        <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${required ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
          {required ? 'Obrigatório' : 'Opcional'}
        </span>
      </div>

      <div data-document-current className="mt-3 rounded-md bg-gray-50 p-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Documento vigente</div>
        {current ? (
          <>
            <div className="mt-1"><VersionSummary document={current} configuredType={configuredType} /></div>
            {current.rejectionReason && <p className="mt-2 text-xs text-red-600">{current.rejectionReason}</p>}
            <div className="mt-2 flex flex-wrap gap-2">
              <OpenDocumentButton document={current} />
              {reviewControls(current)}
            </div>
          </>
        ) : (
          <p className="mt-1 text-xs text-gray-600">Nenhum documento vigente.</p>
        )}
      </div>

      {candidate && (
        <div data-document-candidate className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
            {candidate.status === 'reprovado' ? 'Candidata reprovada' : 'Candidata em análise'}
          </div>
          <div className="mt-1"><VersionSummary document={candidate} configuredType={configuredType} /></div>
          {candidate.rejectionReason && <p className="mt-2 text-xs text-red-600">{candidate.rejectionReason}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            <OpenDocumentButton document={candidate} />
            {reviewControls(candidate)}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onRenew}
          className="rounded-lg border border-blue-300 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
        >
          Enviar nova versão
        </button>
        {history.length > 0 && (
          <details className="text-xs text-gray-600">
            <summary className="cursor-pointer rounded font-medium focus:outline-none focus:ring-2 focus:ring-blue-500">
              Histórico de versões ({history.length})
            </summary>
            <ul className="mt-2 space-y-2">
              {history.map((document) => (
                <li key={document.id} data-document-history-version={document.version} className="rounded border border-gray-200 bg-white p-2">
                  <VersionSummary document={document} configuredType={configuredType} />
                  <div className="mt-1"><OpenDocumentButton document={document} /></div>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
};
